import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, FileText, Search, Copy, Check, Clock } from 'lucide-react';
import { TranscriptSegment } from '../../services/meetingService';

interface TranscriptViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  segments: TranscriptSegment[];
  salesPerson?: string;
  leadName?: string;
  companyName?: string;
  meetingDate?: string;
}

const TranscriptViewerModal: React.FC<TranscriptViewerModalProps> = ({
  isOpen,
  onClose,
  segments,
  salesPerson = '',
  leadName = 'Prospect',
  companyName,
  meetingDate,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollTo({ top: 0 }), 50);
    }
    setSearchTerm('');
    setCopied(false);
  }, [isOpen]);

  const salesNames = useMemo(() => {
    if (!salesPerson) return new Set<string>();
    const parts = salesPerson.toLowerCase().split(/\s+/);
    const names = new Set<string>();
    names.add(salesPerson.toLowerCase());
    if (parts.length >= 2) {
      names.add(parts[0]);
      names.add(parts[parts.length - 1]);
    }
    return names;
  }, [salesPerson]);

  const isSalesTeam = (speaker: string): boolean => {
    if (!speaker || salesNames.size === 0) return false;
    const lower = speaker.toLowerCase().trim();
    if (salesNames.has(lower)) return true;
    const speakerParts = lower.split(/\s+/);
    for (const part of speakerParts) {
      if (part.length > 2 && salesNames.has(part)) return true;
    }
    return false;
  };

  const filteredSegments = useMemo(() => {
    if (!searchTerm.trim()) return segments;
    const term = searchTerm.toLowerCase();
    return segments.filter(
      s =>
        s.text.toLowerCase().includes(term) ||
        s.speaker.toLowerCase().includes(term)
    );
  }, [segments, searchTerm]);

  const handleCopy = async () => {
    const text = segments
      .map(s => `[${s.timestamp}] ${s.speaker}: ${s.text}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const highlightText = (text: string) => {
    if (!searchTerm.trim()) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 rounded px-0.5">{part}</mark>
      ) : (
        part
      )
    );
  };

  if (!isOpen) return null;

  const subtitle = companyName
    ? `${leadName} from ${companyName}${meetingDate ? ` - ${meetingDate}` : ''}`
    : `Meeting${meetingDate ? ` - ${meetingDate}` : ''}`;

  let lastSpeaker = '';

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Meeting Transcript</h3>
              <p className="text-xs text-gray-500 font-medium">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border-2 border-gray-200 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5 text-gray-600 hover:text-[#1875FF]"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search transcript..."
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1875FF]/30 focus:border-[#1875FF] transition-all placeholder-gray-400"
            />
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
          {filteredSegments.length === 0 ? (
            <div className="text-center py-16">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400 text-sm font-medium">
                {searchTerm ? 'No matching segments found' : 'No transcript data'}
              </p>
            </div>
          ) : (
            <div className="py-5 px-5 space-y-3">
              {filteredSegments.map((segment, index) => {
                const isTeam = isSalesTeam(segment.speaker);
                const showName = segment.speaker !== lastSpeaker;
                lastSpeaker = segment.speaker;

                return (
                  <TranscriptBubble
                    key={index}
                    segment={segment}
                    isRight={isTeam}
                    showName={showName}
                    highlightText={highlightText}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400 font-medium">
            {segments.length} segment{segments.length !== 1 ? 's' : ''}
            {searchTerm && filteredSegments.length !== segments.length && (
              <span> ({filteredSegments.length} shown)</span>
            )}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
              Prospect
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#1875FF]" />
              Sales Team
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

interface TranscriptBubbleProps {
  segment: TranscriptSegment;
  isRight: boolean;
  showName: boolean;
  highlightText: (text: string) => React.ReactNode;
}

const TranscriptBubble: React.FC<TranscriptBubbleProps> = ({
  segment,
  isRight,
  showName,
  highlightText,
}) => {
  return (
    <div className={`flex flex-col ${isRight ? 'items-end' : 'items-start'}`}>
      {showName && (
        <span
          className={`text-[11px] font-bold mb-1 px-1 ${
            isRight ? 'text-[#1875FF]' : 'text-gray-500'
          }`}
        >
          {segment.speaker}
        </span>
      )}
      <div
        className={`relative max-w-[78%] px-3.5 py-2.5 ${
          isRight
            ? 'bg-[#1875FF]/10 rounded-lg rounded-tr-sm border border-[#1875FF]/15'
            : 'bg-white rounded-lg rounded-tl-sm border border-gray-200 shadow-sm'
        }`}
      >
        <p
          className={`text-[13.5px] leading-relaxed whitespace-pre-wrap break-words pr-14 ${
            isRight ? 'text-gray-800' : 'text-gray-700'
          }`}
        >
          {highlightText(segment.text)}
        </p>
        <div className="flex items-center gap-1 justify-end -mt-3 float-right ml-3 relative top-1">
          <Clock size={10} className={isRight ? 'text-[#1875FF]/50' : 'text-gray-300'} />
          <span
            className={`text-[10.5px] ${
              isRight ? 'text-[#1875FF]/60' : 'text-gray-400'
            }`}
          >
            {segment.timestamp}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TranscriptViewerModal;
